import React from "react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DatePickerProps {
  date: Date | null;
  onSelect: (date: Date | null) => void;
  label?: string;
  placeholder?: string;
}

export function DatePicker({
  date,
  onSelect,
  label = "Pick a date",
  placeholder = "Pick a date",
}: DatePickerProps) {
  // Simple date picker with month and day selection
  const [month, setMonth] = React.useState(
    date ? date.getMonth() : new Date().getMonth()
  );
  const [year, setYear] = React.useState(
    date ? date.getFullYear() : new Date().getFullYear()
  );

  // Go to previous month
  const prevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  // Go to next month
  const nextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  // Days in month
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // First day of month (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  // Create an array for display
  const days: (number | null)[] = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null); // Empty cells
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  // Weeks (rows in the calendar)
  const weeks: (number | null)[][] = [];
  let week: (number | null)[] = [];

  days.forEach((day, index) => {
    week.push(day);
    if (index % 7 === 6 || index === days.length - 1) {
      weeks.push(week);
      week = [];
    }
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground"
          )}
        >
          {date ? format(date, "PPP") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <Button variant="ghost" size="sm" onClick={prevMonth}>
              &lt;
            </Button>
            <div className="font-medium">
              {format(new Date(year, month, 1), "MMMM yyyy")}
            </div>
            <Button variant="ghost" size="sm" onClick={nextMonth}>
              &gt;
            </Button>
          </div>

          <div className="grid grid-cols-7 text-center text-xs mb-1">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div key={day} className="py-1">
                {day}
              </div>
            ))}
          </div>

          <div className="space-y-1">
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="grid grid-cols-7 gap-1">
                {week.map((day, dayIndex) => {
                  if (day === null) {
                    return (
                      <div
                        key={`empty-${weekIndex}-${dayIndex}`}
                        className="h-8 w-8"
                      />
                    );
                  }

                  const currentDate = new Date(year, month, day);
                  const isSelected =
                    date &&
                    date.getDate() === day &&
                    date.getMonth() === month &&
                    date.getFullYear() === year;

                  const isToday =
                    new Date().getDate() === day &&
                    new Date().getMonth() === month &&
                    new Date().getFullYear() === year;

                  return (
                    <Button
                      key={`day-${weekIndex}-${dayIndex}`}
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-8 w-8 p-0 font-normal",
                        isSelected &&
                          "bg-primary text-primary-foreground hover:bg-primary/90",
                        isToday && !isSelected && "border border-primary/50"
                      )}
                      onClick={() => onSelect(currentDate)}
                    >
                      {day}
                    </Button>
                  );
                })}
              </div>
            ))}
          </div>

          {date && (
            <div className="mt-3 border-t pt-3 flex justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSelect(new Date())}
              >
                Today
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onSelect(null)}>
                Clear
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
